import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { memberProfileAPI } from '../../services/api';

export interface MemberSearchOption {
  id: string;
  firstName: string;
  lastName: string;
  membershipNumber?: string | null;
  membershipTierName?: string | null;
  /** Display label. For options that came from the server this is "First Last". */
  label: string;
}

interface MemberSearchAutocompleteProps {
  /** Currently selected member (id + label), or null. */
  value: { id: string; label: string } | null;
  onSelect: (option: MemberSearchOption | null) => void;
  /** Member profile ids that must not be offered (e.g. the host, or players already added). */
  excludeIds?: string[];
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * Server-side member picker for member-facing screens. Uses GET /member-profiles/search, which returns only
 * name / membership number / tier (no email, phone or payment info) and is paged - members are not allowed
 * to download the whole member list.
 */
const MemberSearchAutocomplete: React.FC<MemberSearchAutocompleteProps> = ({
  value,
  onSelect,
  excludeIds = [],
  label = 'Select Member',
  size,
  fullWidth = true,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<MemberSearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    const query = inputValue.trim();
    // Don't search for the text of the option that is already selected
    if (query.length < MIN_QUERY_LENGTH || (value && query === value.label)) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await memberProfileAPI.searchMemberProfiles(query, 20);
        if (seq !== requestSeq.current) return; // a newer query superseded this one
        const results = (response.data?.results || []) as Omit<MemberSearchOption, 'label'>[];
        setOptions(results.map(r => ({ ...r, label: `${r.firstName} ${r.lastName}`.trim() })));
      } catch {
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, value]);

  const visibleOptions = useMemo(
    () => options.filter(o => !excludeIds.includes(o.id)),
    [options, excludeIds]
  );
  const selectedOption: MemberSearchOption | null = value
    ? { id: value.id, firstName: '', lastName: '', label: value.label }
    : null;

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      options={visibleOptions}
      value={selectedOption}
      loading={loading}
      filterOptions={x => x} // the server already filtered
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={o => o.label}
      onChange={(_, newValue) => onSelect(newValue)}
      onInputChange={(_, text, reason) => {
        if (reason === 'input') setInputValue(text);
        if (reason === 'clear') setInputValue('');
      }}
      noOptionsText={inputValue.trim().length < MIN_QUERY_LENGTH ? 'Type at least 2 letters of a name or membership number' : 'No members found'}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as any;
        return (
          <li key={key} {...otherProps}>
            <div>
              <div>{option.label}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {[option.membershipNumber, option.membershipTierName].filter(Boolean).join(' • ') || 'Member'}
              </div>
            </div>
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by name or membership number..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MemberSearchAutocomplete;
